// ===========================================
// Absence Notification React Query Hooks
// ===========================================
// Custom hooks for absence notification API operations

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  absenceNotificationApi,
  CreateAbsenceInput,
  AbsenceNotificationFilters,
  StudentAbsenceFilters,
} from '../api/absenceNotification.api';
import { calendarKeys } from './useHybridBooking';
import { attendanceKeys } from './useAttendance';

// ===========================================
// QUERY KEYS
// ===========================================

export const absenceKeys = {
  all: ['absences'] as const,
  lists: () => [...absenceKeys.all, 'list'] as const,
  list: (filters?: AbsenceNotificationFilters) =>
    [...absenceKeys.lists(), filters] as const,
  student: (studentId: string, filters?: StudentAbsenceFilters) =>
    [...absenceKeys.all, 'student', studentId, filters] as const,
  detail: (id: string) => [...absenceKeys.all, 'detail', id] as const,
  affectedLessons: (studentId: string, startDate: string, endDate: string) =>
    [...absenceKeys.all, 'affected', studentId, startDate, endDate] as const,
  affectedSessions: (id: string) =>
    [...absenceKeys.all, 'affected-sessions', id] as const,
};

// ===========================================
// PARENT QUERIES
// ===========================================

/**
 * Get affected lessons for a date range (preview before submitting)
 */
export function useAffectedLessons(
  studentId: string,
  startDate: string,
  endDate: string
) {
  return useQuery({
    queryKey: absenceKeys.affectedLessons(studentId, startDate, endDate),
    queryFn: () =>
      absenceNotificationApi.getAffectedLessons(studentId, startDate, endDate),
    enabled: !!studentId && !!startDate && !!endDate,
  });
}

/**
 * Get absence notifications for a specific student
 */
export function useStudentAbsences(
  studentId: string,
  filters?: StudentAbsenceFilters
) {
  return useQuery({
    queryKey: absenceKeys.student(studentId, filters),
    queryFn: () =>
      absenceNotificationApi.getStudentAbsences(studentId, filters),
    enabled: !!studentId,
  });
}

/**
 * Get a single absence notification by ID
 */
export function useAbsenceNotification(id: string) {
  return useQuery({
    queryKey: absenceKeys.detail(id),
    queryFn: () => absenceNotificationApi.getById(id),
    enabled: !!id,
  });
}

// ===========================================
// ADMIN QUERIES
// ===========================================

/**
 * Get all absence notifications (admin view)
 */
export function useAllAbsences(filters?: AbsenceNotificationFilters) {
  return useQuery({
    queryKey: absenceKeys.list(filters),
    queryFn: () => absenceNotificationApi.getAll(filters),
  });
}

/**
 * Get all affected sessions in the absence window, with per-row `releasable` flags.
 * Powers the per-lesson release toggles in the admin "Confirm Absence" modal.
 */
export function useAbsenceAffectedSessions(absenceId: string | null) {
  return useQuery({
    queryKey: absenceKeys.affectedSessions(absenceId ?? ''),
    queryFn: () => absenceNotificationApi.getAffectedSessions(absenceId!),
    enabled: !!absenceId,
  });
}

// ===========================================
// MUTATIONS
// ===========================================

/**
 * Create a new absence notification
 */
export function useCreateAbsence() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreateAbsenceInput) => absenceNotificationApi.create(data),
    onSuccess: (result) => {
      // Invalidate all absence queries
      queryClient.invalidateQueries({ queryKey: absenceKeys.all });
      // Invalidate calendar (attendance might be shown)
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      // The teacher roster badge reads the notification — refresh it too (2026-09-07).
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
      enqueueSnackbar(
        `Absence notification submitted for ${result.student.firstName}`,
        { variant: 'success' }
      );
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to submit absence notification';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Cancel an absence notification
 */
export function useCancelAbsence() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => absenceNotificationApi.cancel(id),
    onSuccess: (result) => {
      // Invalidate all absence queries
      queryClient.invalidateQueries({ queryKey: absenceKeys.all });
      // Invalidate calendar
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      // The teacher roster badge reads the notification — refresh it too (2026-09-07).
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
      enqueueSnackbar(
        `Absence notification cancelled for ${result.student.firstName}`,
        { variant: 'success' }
      );
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to cancel absence notification';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Confirm an absence (admin) and optionally release teacher time slots for selected
 * 1:1 sessions. When `releaseSessions` is omitted, releases ALL releasable sessions
 * (backwards-compat). Empty array means confirm only without releasing.
 */
export function useReleaseAbsenteeSlot() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      id,
      useMakeupCredit,
      releaseSessions,
    }: {
      id: string;
      useMakeupCredit?: boolean;
      releaseSessions?: Array<{ lessonId: string; lessonDate: string }>;
    }) => absenceNotificationApi.releaseSlot(id, { useMakeupCredit, releaseSessions }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: absenceKeys.all });
      const creditMsg = result.makeupCredit ? ' · 1 makeup credit used' : '';
      const baseMsg = result.slotsCreated === 0
        ? 'Absence confirmed'
        : `Absence confirmed · ${result.slotsCreated} slot${result.slotsCreated !== 1 ? 's' : ''} released for booking`;
      enqueueSnackbar(`${baseMsg}${creditMsg}`, { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to confirm absence';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}
